import { supabase } from '../utils/supabase';

class AuthService {
  // Sign in with email and password
  async signIn(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        return { success: false, error: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' };
      }
      return { success: false, error: 'Something went wrong during sign in' };
    }
  }

  // Sign up with email and password
  async signUp(email, password, userData = {}) {
    try {
      // Step 1: Create auth user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: userData?.fullName || '',
            role: userData?.role || 'user'
          }
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      // Step 2: Update user profile with additional data (wedding date, etc.)
      if (data?.user?.id) {
        // Generate referral code if not provided
        const referralCode = this.generateReferralCode(userData?.fullName || email);

        // Update user_profiles with wedding and personal data
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({
            phone: userData?.phone || null,
            wedding_date: userData?.weddingDate || null,
            partner_name: userData?.partnerName || null,
            wedding_budget: userData?.budget || null,
            guest_count: userData?.guestCount || null,
            city: userData?.city || null,
            state: userData?.state || null,
            culture: userData?.culturalBackground || userData?.culture || null,
            location: userData?.location || null,
            referral_code: referralCode,
            referred_by: userData?.referralCode || null
          })
          .eq('id', data.user.id);

        if (profileError) {
          console.error('Profile update error:', profileError);
          // Don't fail signup if profile update fails, user can update later
        }

        // Step 3: If user used a referral code, create referral record
        if (userData?.referralCode) {
          try {
            // Find the referrer
            const { data: referrer } = await supabase
              .from('user_profiles')
              .select('id')
              .eq('referral_code', userData.referralCode)
              .maybeSingle();

            if (referrer) {
              // Create referral record
              await supabase
                .from('user_referrals')
                .insert({
                  referrer_user_id: referrer.id,
                  referred_user_id: data.user.id,
                  referred_email: email,
                  referral_code: userData.referralCode,
                  status: 'completed', // Mark as completed since signup happened
                  reward_amount: 25
                });
            }
          } catch (referralError) {
            console.error('Referral creation error:', referralError);
            // Don't fail signup if referral creation fails
          }
        }
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        return { success: false, error: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' };
      }
      return { success: false, error: 'Something went wrong during sign up' };
    }
  }

  // Generate a unique referral code
  generateReferralCode(name) {
    const namePart = (name || 'USER')
      .replace(/[^a-zA-Z]/g, '')
      .toUpperCase()
      .slice(0, 4)
      .padEnd(4, 'X');
    const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${namePart}${randomPart}`;
  }

  // Sign out
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to authentication service. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Something went wrong during sign out' };
    }
  }

  // Get current session
  async getSession() {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to authentication service. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Something went wrong getting session' };
    }
  }

  // Get user profile
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Something went wrong loading profile' };
    }
  }

  // Update user profile
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Something went wrong updating profile' };
    }
  }

  // Reset password
  async resetPassword(email) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to authentication service. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Something went wrong sending reset email' };
    }
  }

  // Listen to auth state changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  }

  // Sign in with OAuth (Google, Facebook, etc.)
  async signInWithOAuth(provider) {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { success: false, error: 'Cannot connect to authentication service. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' };
      }
      return { success: false, error: 'Something went wrong with social sign in' };
    }
  }

  // Get current user
  getCurrentUser() {
    return supabase.auth.getUser();
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;